
import * as React from 'react'
import { FixedSizeList as List } from 'react-window';
import { I18n } from "react-redux-i18n";
import { Flex } from '@common/components/Flex';
import { SelectedFolderSecuritySetting, FolderSettingDto, FolderSettingUpdateDto, FolderSecuritySettingKey } from '@common/dto/folder-settings';
import { getTestAttributes } from '@common/utils/tests';
import { Ellipsis } from '@common/components/Ellipsis';

import { FolderSecuritySettingTable } from '../../folder-management.types';

import './settings.styles.scss';

type SettingsProps = {
  folderSecuritySettings?: Record<FolderSettingDto['id'], FolderSettingDto>;
  overrideSec: FolderSettingUpdateDto['overrideSec'];
  selectedFolderSecuritySetting?: Record<FolderSettingDto['secIndex'], SelectedFolderSecuritySetting>;
  onSettingChange: (value: Record<FolderSettingDto['secIndex'], SelectedFolderSecuritySetting>) => void
  onOverrideSecChange: (value: FolderSettingUpdateDto['overrideSec']) => void;
};

type IsCheckedParams = Pick<FolderSettingDto, 'secIndex' | 'secPosition'>;

type SettingCheckboxDataset = {
  featureField: FolderSecuritySettingKey;
  groupId: number;
};

type SettingListItem = {
  index: number;
  data: FolderSettingDto[];
  style: React.CSSProperties;
};

const nameField = 'name';

export const Settings = ({ folderSecuritySettings, onSettingChange, onOverrideSecChange, selectedFolderSecuritySetting, overrideSec }: SettingsProps) => {
  const columns: FolderSecuritySettingTable[] = React.useMemo(() => [
    { title: 'Class Name', key: nameField },
    { title: I18n.t('Read Folder'), key: 'readFoldSec' },
    { title: I18n.t('Add Ped'), key: 'addSec' },
    { title: I18n.t('Del Ped'), key: 'delSec' },
    { title: I18n.t('Modify Ped'), key: 'modSec' },
    { title: I18n.t('Ped Read'), key: 'readSec', disable: !overrideSec },
    { title: I18n.t('Ped Write'), key: 'writeSec', disable: !overrideSec },
  ], [overrideSec]);

  const folderSecuritySettingsList = React.useMemo(() => Object.values(folderSecuritySettings || {}), [folderSecuritySettings]);

  const getGroupKey = React.useCallback((index: number, data: FolderSettingDto[]) => {
    return data[index]!.id;
  }, []);

  const isChecked = React.useCallback((setting: IsCheckedParams, featureFieldName: FolderSecuritySettingKey) => {
    const config = selectedFolderSecuritySetting?.[setting.secIndex];
    const featureSecPosition = config?.[featureFieldName] ?? null;

    if (!setting.secPosition === null || featureSecPosition === null) {
      return false;
    }

    return (featureSecPosition & (1 << (setting.secPosition - 1))) > 0;
  }, [selectedFolderSecuritySetting]);


  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { dataset, checked } = e.target;
    const { featureField, groupId } = dataset as unknown as SettingCheckboxDataset;

    const group = folderSecuritySettings![groupId]!;
    const config = selectedFolderSecuritySetting?.[group.secIndex] as SelectedFolderSecuritySetting;
    let featureSecPosition = config?.[featureField] as number;

    if (checked) {
      featureSecPosition = featureSecPosition | (1 << (group.secPosition - 1));
    } else {
      featureSecPosition = featureSecPosition & ~(1 << (group.secPosition - 1));
    }

    const newSecuritySettings = {
      ...selectedFolderSecuritySetting,
      [group.secIndex]: {
        ...config,
        secIndex: group.secIndex,
        [featureField]: featureSecPosition
      },
    };

    onSettingChange(newSecuritySettings);

  }, [folderSecuritySettings, selectedFolderSecuritySetting, onSettingChange]);

  const handleOverrideSecCheckboxesChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onOverrideSecChange(e.target.checked);
  }, [onOverrideSecChange]);

  const cellWidth = `${100 / columns.length}%`;

  const renderGroup = React.useCallback(({ index, data, style }: SettingListItem) => {
    const item = data[index] as FolderSettingDto;

    return (
      <Flex
        justify="flex-start"
        alignItems="center"
        style={style}
        className="FolderManagement__folder-setting__groups-list__item"
      >
        {columns.map((x, i) => {
          if (x.key === nameField) {
            return (
              <Ellipsis
                width={cellWidth}
                text={item.name}
                key={i}
                {...getTestAttributes({ id: `folder-settings-grid-0-0` })}
              />
            );
          }

          return (
            <input
              {...getTestAttributes({ id: `folder-settings-grid-${index + 1}-${i + 1}` })}
              key={i}
              style={{ width: cellWidth }}
              type="checkbox"
              checked={isChecked({ secIndex: item.secIndex, secPosition: item.secPosition }, x.key)}
              data-feature-field={x.key}
              data-group-id={item.id}
              onChange={handleChange}
              className="FolderManagement__folder-setting__groups-list__checkbox"
              disabled={x.disable}
            />
          );
        })}
      </Flex>
    );
  }, [isChecked, handleChange, columns]);


  return (
    <>
      <div className="FolderManagement__folder-setting__overrideSec">
        <input
          {...getTestAttributes({ id: 'cbx-folder-settings-override-sec-input' })}
          className="FolderManagement__folder-setting__overrideSec__checkbox"
          type="checkbox"
          checked={overrideSec}
          onChange={handleOverrideSecCheckboxesChange}
        />
        <label
          {...getTestAttributes({ id: 'lbl-folder-settings-override-sec' })}
          htmlFor={getTestAttributes({ id: 'cbx-folder-settings-override-sec-input' }).id}
          className='FolderManagement__folder-setting__overrideSec__checkbox__label'
        >
          {I18n.t('Override Pedigree Level Security')}
        </label>

      </div>
      <Flex className="FolderManagement__folder-setting__groups-header" width="100%" justify="flex-start">
        {columns.map((x, columnIndex) => (
          <Ellipsis
            className="FolderManagement__folder-setting__groups-header__item"
            {...getTestAttributes({ id: `folder-settings-grid-0-${columnIndex}` })}
            width={cellWidth}
            text={x.title}
            key={x.title}
          />
        ))}
      </Flex>
      <List
        className="FolderManagement__folder-setting__groups-list"
        height={140}
        itemCount={folderSecuritySettingsList.length}
        itemSize={26}
        width="100%"
        overscanCount={5}
        itemData={folderSecuritySettingsList}
        itemKey={getGroupKey}
      >
        {renderGroup}
      </List>
    </>
  )
}